import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ReportDataService from '../../../services/report.service';
import AttendanceService from '../../../services/attendance.service';
import CycleService from '../../../services/cycle.service';
import { useTranslation } from '../../../hooks/useTranslation';
import { useAuth } from '../../../contexts/AuthContext';

const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const monthAgoInput = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const AttendanceStudentSummaryReport = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  const [from, setFrom] = useState(monthAgoInput());
  const [to, setTo] = useState(todayInput());
  const [cycleId, setCycleId] = useState('');
  const [cycles, setCycles] = useState([]);
  const [filterBy, setFilterBy] = useState('grade');
  const [gradeId, setGradeId] = useState('');
  const [cursoId, setCursoId] = useState('');
  const [scopes, setScopes] = useState({ grades: [], cursos: [], can_manage_all: true });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('student_name');
  const [sortDir, setSortDir] = useState('asc');

  const canManageAll = scopes.can_manage_all || hasPermission('attendances.manage');

  const loadFilters = useCallback(async () => {
    const [cyclesRes, scopesRes] = await Promise.all([
      CycleService.getAll(),
      AttendanceService.getScopes({ cycle_id: cycleId || undefined }),
    ]);
    const cycleList = cyclesRes.data?.data || [];
    setCycles(cycleList);
    const scopeData = scopesRes.data?.data || {};
    setScopes(scopeData);
    setCycleId((prev) => prev || (cycleList.length ? String(cycleList[cycleList.length - 1].id) : ''));
    setGradeId((prev) => prev || (scopeData.grades?.[0] ? String(scopeData.grades[0].id) : ''));
    setCursoId((prev) => prev || (scopeData.cursos?.[0] ? String(scopeData.cursos[0].id) : ''));
  }, [cycleId]);

  useEffect(() => {
    loadFilters().catch(() => setError(t('reports.errorLoading')));
  }, []);

  useEffect(() => {
    if (!cycleId) return;
    AttendanceService.getScopes({ cycle_id: cycleId })
      .then((res) => {
        const scopeData = res.data?.data || {};
        setScopes(scopeData);
        if (!scopeData.grades?.some((g) => String(g.id) === gradeId)) {
          setGradeId(scopeData.grades?.[0] ? String(scopeData.grades[0].id) : '');
        }
        if (!scopeData.cursos?.some((c) => String(c.id) === cursoId)) {
          setCursoId(scopeData.cursos?.[0] ? String(scopeData.cursos[0].id) : '');
        }
      })
      .catch(() => {});
  }, [cycleId]);

  const load = async () => {
    if (!cycleId || !from || !to) return;
    if (filterBy === 'grade' && !gradeId) return;
    if (filterBy === 'curso' && !cursoId) return;

    try {
      setLoading(true);
      setError('');
      const params = {
        from,
        to,
        cycle_id: cycleId,
        filter_by: filterBy,
      };
      if (filterBy === 'grade') params.grade_id = gradeId;
      if (filterBy === 'curso') params.curso_id = cursoId;

      const res = await ReportDataService.getAttendanceStudentSummary(params);
      setData(res.data?.data || null);
      setStatusFilter('all');
      setSortBy('student_name');
      setSortDir('asc');
    } catch (e) {
      setError(e.response?.data?.message || t('reports.errorLoading'));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const totals = data?.totals || {};
  const allRows = data?.rows || [];

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir(column === 'student_name' ? 'asc' : 'desc');
    }
  };

  const filteredRows = useMemo(() => {
    let rows = allRows;
    switch (statusFilter) {
      case 'present':
        rows = rows.filter((r) => r.present > 0);
        break;
      case 'absent':
        rows = rows.filter((r) => r.absent > 0);
        break;
      case 'late':
        rows = rows.filter((r) => r.late > 0);
        break;
      case 'unmarked':
        rows = rows.filter((r) => r.unmarked > 0);
        break;
      default:
        break;
    }

    return [...rows].sort((a, b) => {
      if (sortBy === 'student_name') {
        const cmp = (a.student_name || '').localeCompare(b.student_name || '', undefined, { sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [allRows, statusFilter, sortBy, sortDir]);

  const statusFilterOptions = [
    { value: 'all', label: t('reports.attendanceStatusAll') },
    { value: 'present', label: t('reports.attendanceStatusPresent') },
    { value: 'absent', label: t('reports.attendanceStatusAbsent') },
    { value: 'late', label: t('reports.attendanceStatusLate') },
    { value: 'unmarked', label: t('reports.attendanceStatusUnmarked') },
  ];

  const filterReady = useMemo(() => {
    if (!cycleId || !from || !to) return false;
    if (filterBy === 'grade') return Boolean(gradeId);
    if (filterBy === 'curso') return Boolean(cursoId);
    return true;
  }, [cycleId, cursoId, filterBy, from, gradeId, to]);

  return (
    <Box>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
        {t('reports.attendanceStudentSummary')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('reports.attendanceStudentSummaryDescription')}
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            type="date"
            label={t('reports.startDate')}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label={t('reports.endDate')}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t('attendance.cycle')}</InputLabel>
            <Select label={t('attendance.cycle')} value={cycleId} onChange={(e) => setCycleId(e.target.value)}>
              {cycles.map((cycle) => (
                <MenuItem key={cycle.id} value={String(cycle.id)}>
                  {cycle.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={filterBy}
            onChange={(_, v) => v && setFilterBy(v)}
          >
            <ToggleButton value="grade">{t('attendance.byGrade')}</ToggleButton>
            <ToggleButton value="curso">{t('attendance.byCourse')}</ToggleButton>
            {canManageAll && (
              <ToggleButton value="school">{t('reports.attendanceSchoolWide')}</ToggleButton>
            )}
          </ToggleButtonGroup>

          {filterBy === 'grade' && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t('attendance.grade')}</InputLabel>
              <Select label={t('attendance.grade')} value={gradeId} onChange={(e) => setGradeId(e.target.value)}>
                {(scopes.grades || []).map((grade) => (
                  <MenuItem key={grade.id} value={String(grade.id)}>
                    {grade.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {filterBy === 'curso' && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>{t('attendance.course')}</InputLabel>
              <Select label={t('attendance.course')} value={cursoId} onChange={(e) => setCursoId(e.target.value)}>
                {(scopes.cursos || []).map((curso) => (
                  <MenuItem key={curso.id} value={String(curso.id)}>
                    {curso.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button variant="contained" onClick={load} disabled={loading || !filterReady}>
            {loading ? <CircularProgress size={20} /> : t('reports.filter')}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && (
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={t('reports.attendanceDaysInRange', { count: data.total_days })} />
            <Chip
              color="success"
              variant={statusFilter === 'present' ? 'filled' : 'outlined'}
              clickable
              onClick={() => setStatusFilter((prev) => (prev === 'present' ? 'all' : 'present'))}
              label={t('attendance.summaryPresent', { count: totals.present || 0 })}
            />
            <Chip
              color="error"
              variant={statusFilter === 'absent' ? 'filled' : 'outlined'}
              clickable
              onClick={() => setStatusFilter((prev) => (prev === 'absent' ? 'all' : 'absent'))}
              label={t('attendance.summaryAbsent', { count: totals.absent || 0 })}
            />
            <Chip
              color="warning"
              variant={statusFilter === 'late' ? 'filled' : 'outlined'}
              clickable
              onClick={() => setStatusFilter((prev) => (prev === 'late' ? 'all' : 'late'))}
              label={t('attendance.summaryLate', { count: totals.late || 0 })}
            />
            <Chip
              variant={statusFilter === 'unmarked' ? 'filled' : 'outlined'}
              clickable
              onClick={() => setStatusFilter((prev) => (prev === 'unmarked' ? 'all' : 'unmarked'))}
              label={t('reports.attendanceUnmarked', { count: totals.unmarked || 0 })}
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t('reports.attendanceStatusFilter')}</InputLabel>
              <Select
                label={t('reports.attendanceStatusFilter')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusFilterOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {t('reports.attendanceShowingCount', {
                count: filteredRows.length,
                total: allRows.length,
              })}
            </Typography>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortBy === 'student_name' ? sortDir : false}>
                    <TableSortLabel
                      active={sortBy === 'student_name'}
                      direction={sortBy === 'student_name' ? sortDir : 'asc'}
                      onClick={() => handleSort('student_name')}
                    >
                      {t('attendance.student')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t('attendance.code')}</TableCell>
                  <TableCell>{t('attendance.grade')}</TableCell>
                  <TableCell align="right" sortDirection={sortBy === 'present' ? sortDir : false}>
                    <TableSortLabel
                      active={sortBy === 'present'}
                      direction={sortBy === 'present' ? sortDir : 'asc'}
                      onClick={() => handleSort('present')}
                      sx={{ flexDirection: 'row-reverse' }}
                    >
                      {t('attendance.present')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortBy === 'absent' ? sortDir : false}>
                    <TableSortLabel
                      active={sortBy === 'absent'}
                      direction={sortBy === 'absent' ? sortDir : 'asc'}
                      onClick={() => handleSort('absent')}
                      sx={{ flexDirection: 'row-reverse' }}
                    >
                      {t('attendance.absent')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortBy === 'late' ? sortDir : false}>
                    <TableSortLabel
                      active={sortBy === 'late'}
                      direction={sortBy === 'late' ? sortDir : 'asc'}
                      onClick={() => handleSort('late')}
                      sx={{ flexDirection: 'row-reverse' }}
                    >
                      {t('attendance.late')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortBy === 'unmarked' ? sortDir : false}>
                    <TableSortLabel
                      active={sortBy === 'unmarked'}
                      direction={sortBy === 'unmarked' ? sortDir : 'asc'}
                      onClick={() => handleSort('unmarked')}
                      sx={{ flexDirection: 'row-reverse' }}
                    >
                      {t('reports.attendanceUnmarkedDays')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {statusFilter === 'all' ? t('attendance.empty') : t('reports.attendanceNoStudentsForFilter')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row) => (
                    <TableRow key={`${row.student_id}-${row.grade_id}`} hover>
                      <TableCell>{row.student_name}</TableCell>
                      <TableCell>{row.student_code || '—'}</TableCell>
                      <TableCell>{row.grade_name}</TableCell>
                      <TableCell align="right">{row.present}</TableCell>
                      <TableCell align="right">{row.absent}</TableCell>
                      <TableCell align="right">{row.late}</TableCell>
                      <TableCell align="right">{row.unmarked}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default AttendanceStudentSummaryReport;
